import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Heart, Loader2, Leaf } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import { useFavorites } from '../context/FavoritesContext';
import { devError } from '../utils/logger';
import ListingCard from '../components/ListingCard';
import { Button } from '../components/ui/button';

export default function FavoritesPage() {
  const { ids } = useFavorites();
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/favorites`);
        if (cancelled) return;
        setFavorites(data.favorites || []);
      } catch (e) {
        devError('favorites load failed', e);
        if (!cancelled) toast.error('Could not load your favorites');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // We re-fetch when the favorited ids set size changes (add/remove via heart button on a card)
  }, [ids.size]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" data-testid="favorites-loading">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Your collection</p>
          <h1 className="font-playfair text-3xl md:text-4xl font-bold text-foreground mb-2">
            Saved Listings
          </h1>
          <p className="text-muted-foreground text-sm">
            {favorites.length === 0
              ? 'You haven\'t saved anything yet — tap the heart on a listing to keep it here.'
              : `${favorites.length} item${favorites.length === 1 ? '' : 's'} you\'re keeping an eye on.`}
          </p>
          {favorites.length > 0 && (
            <Link
              to={`/sustainability-report?ids=${favorites.map((l) => l.listing_id).join(',')}`}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-xs transition-colors"
              data-testid="favorites-report-link"
              onClick={(e) => {
                e.preventDefault();
                const ids = favorites.map((l) => l.listing_id).join('\n');
                sessionStorage.setItem('patinamart-prefill-ids', ids);
                window.location.href = '/sustainability-report';
              }}
            >
              <Leaf className="w-3 h-3" /> Generate Sustainability Report from these
            </Link>
          )}
        </div>

        {favorites.length === 0 ? (
          <div className="text-center py-16 rounded-xl border border-border/20 bg-card/30" data-testid="favorites-empty">
            <Heart className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground mb-6">No favorites yet.</p>
            <Link to="/marketplace">
              <Button className="btn-gold" data-testid="favorites-browse-btn">Browse marketplace</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-grid">
            {favorites.map((listing) => (
              <ListingCard key={listing.listing_id} listing={listing} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
